import React, { useEffect, useMemo, useState, useRef, useContext } from "react";
import { DateTime } from "luxon";
import styles from "./slots.module.css";
import {
  getDayOffRange,
  getGameRooms,
  getOccupiedSlots,
  getTimeSlotTemplates,
} from "../../api/SlotsApi.js";
import SlotTooltip from "./SlotTooltip";
import { useNavigate } from "react-router-dom";
import useIsMobile from "@/shared/lib/hooks/useIsMobile";
import common from '../../common.module.scss';
import { EmployeeContext } from '../../context/EmployeeContext.jsx';


const ZONE = "Europe/Saratov";

function toDDMMYY(dt) {
  return dt.toFormat("dd.MM.yy");
}

function weekdayShortRu(dt) {
  return dt.setLocale("ru").toFormat("ccc");
}

function buildWeekPeriod(weekStartDt) {
  const start = weekStartDt.startOf("day");
  const end = weekStartDt.plus({ days: 6 }).startOf("day");
  return { start, end };
}

function isoDate(dt) {
  return dt.toFormat("yyyy-MM-dd");
}

function timeLabelHHmm(timeHHmmss) {
  return String(timeHHmmss ?? "").slice(0, 5);
}

export default function Slots({ onPick }) {
  const [weekStart, setWeekStart] = useState(() =>
    DateTime.now().setZone(ZONE).startOf("week")
  );

  const { start, end } = useMemo(() => buildWeekPeriod(weekStart), [weekStart]);
  const startIso = useMemo(() => isoDate(start), [start]);
  const endIso = useMemo(() => isoDate(end), [end]);

  const [daysIso, setDaysIso] = useState([]); // только выходные/праздники
  const [rooms, setRooms] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [occupiedMap, setOccupiedMap] = useState(() => new Set());
  const [loading, setLoading] = useState(false);
  const [tooltip, setTooltip] = useState(null);

  const tooltipRef = useRef(null);
  const handlePick = onPick ?? ((payload) => console.log("pick:", payload));

  const isMobile = useIsMobile();

  const navigate = useNavigate();
     const { employee } = useContext(EmployeeContext);
      const role = employee?.role;

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);

      try {
        // 1) календарь: берем ТОЛЬКО выходные/праздники
        const calRes = await getDayOffRange(startIso, endIso);
        const calArr = Array.isArray(calRes.data) ? calRes.data : [];

        const weekendIso = calArr
          .filter((d) => d?.dayOff === true) // строго выходной/праздник
          .map((d) => d?.date)
          .filter(Boolean)
          .sort();

        // 2) залы
        const roomsRes = await getGameRooms({ page: 0, size: 100 });
        const roomsArr = Array.isArray(roomsRes.data)
          ? roomsRes.data
          : roomsRes.data?.content ?? roomsRes.data?.items ?? [];

        const mappedRooms = roomsArr.map((x) => ({
          id: x.id,
          name: x.name ?? x.title ?? `Room ${x.id}`,
        }));

        // 3) шаблоны слотов (только активные)
        const tplRes = await getTimeSlotTemplates();
        const tplArr = Array.isArray(tplRes.data) ? tplRes.data : [];

        const activeTpl = tplArr
          .filter((t) => t?.isActive !== false)
          .map((t) => ({
            id: t.id,
            startTime: t.startTime, // "HH:mm:ss"
            endTime: t.endTime, // "HH:mm:ss"
            label: timeLabelHHmm(t.startTime), // "HH:mm"
          }))
          .sort((a, b) => String(a.startTime).localeCompare(String(b.startTime)));

        // 4) занятые слоты (для disable)
        const occRes = await getOccupiedSlots(startIso, endIso);
        const occArr = Array.isArray(occRes.data) ? occRes.data : [];

        const map = new Map();
        for (const o of occArr) {
          if (!o?.roomId || !o?.slotDate || o?.slotTemplateId == null) continue;
          const key = `${o.roomId}|${o.slotDate}|slot:${o.slotTemplateId}`;
          map.set(key, o);
        }

        if (!cancelled) {
          setDaysIso(weekendIso);
          setRooms(mappedRooms);
          setTemplates(activeTpl);
          setOccupiedMap(map);
        }
      } catch (e) {
        console.error("slots load error:", e);
        if (!cancelled) {
          setDaysIso([]);
          setRooms([]);
          setTemplates([]);
          setOccupiedMap(new Set());
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [startIso, endIso]);

  useEffect(() => {
    // Закрываем тултип при клике вне его
    function handleClickOutside(event) {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target)) {
        setTooltip(null);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [tooltip]);

  const goPrevWeek = () => setWeekStart((prev) => prev.minus({ weeks: 1 }));
  const goNextWeek = () => setWeekStart((prev) => prev.plus({ weeks: 1 }));

  // Функция для показа тултипа при клике
  const handleSlotClick = (e, room, dateIso, tpl) => {
    const key = `${room.id}|${dateIso}|slot:${tpl.id}`;
    const occ = occupiedMap.get(key);
    
    if (!occ) {
      // Если слот свободен - вызываем onPick
      handlePick({
        mode: "SLOT",
        gameRoomId: room.id,
        startedDate: dateIso,
        slotTemplateId: tpl.id,
      });
      // Закрываем тултип если он был открыт
      setTooltip(null);
      return;
    }

    // Если слот занят - показываем тултип
    // Сначала проверяем, открыт ли уже тултип для этого же слота
    if (tooltip && tooltip.occ === occ) {
      // Если уже открыт для этого слота - закрываем
      setTooltip(null);
    } else {
      // Иначе открываем новый
      const rect = e.currentTarget.getBoundingClientRect();
      setTooltip({
        x: rect.left + rect.width / 2,
        y: rect.top - 8,
        occ,
        roomNameFallback: room.name,
      });
    }
  };

  // Функция для закрытия тултипа
  const closeTooltip = () => {
    setTooltip(null);
  };

  return (
    <section className={styles.section}>
      {isMobile && (<h1 className={common.mobileHeading}>Слоты</h1>)}
      
      <div className={styles.topRow}>
        <div className={styles.weekNav}>
          <button className={styles.navBtn} onClick={goPrevWeek} aria-label="Предыдущая неделя">
            ←
          </button>

          <div className={styles.weekTitle}>
            {toDDMMYY(start)} — {toDDMMYY(end)}
          </div>

          <button className={styles.navBtn} onClick={goNextWeek} aria-label="Следующая неделя">
            →
          </button>
        </div>

        <div className={styles.actions}>
          {/* <button className={styles.iconBtn} type="button" title="Фильтры">
            <span className={styles.slidersIcon} aria-hidden />
          </button> */}
            {role !== "ADMIN" && role !== "MASTER" && isMobile && (
                <button
                    className="service-btn col-auto w-100 mt-4"
                    onClick={() => navigate(`/admin/schedule/create`)}
                >
                    Добавить игру
                </button>
            )}
            {role !== "ADMIN" && role !== "MASTER" && !isMobile && (
                <button
                    className="service-btn service-btn col-auto ms-3"
                    onClick={() => navigate(`/admin/schedule/create`)}
                >
                    Добавить игру
                </button>
            )}
        </div>
      </div>

      {loading && <div className={styles.loading}>Загрузка…</div>}

      <div className={styles.daysList}>
        {daysIso.map((dateIso) => {
          const dayDt = DateTime.fromISO(dateIso, { zone: ZONE }).startOf("day");

          return (
            <div key={dateIso} className={styles.dayRow}>
              <div className={styles.dayLabel}>
                <div className={styles.dayDate}>{toDDMMYY(dayDt)}</div>
                <div className={styles.dayWeek}>{weekdayShortRu(dayDt)}</div>

                {/* если нет в css — можно убрать */}
                {/* <div className={styles.dayMeta}>
                  <span className={styles.dayBadge}>Выходной</span>
                </div> */}
              </div>

              <div className={styles.tableCard}>
                <div className={styles.tableHeader}>
                  <div className={styles.colRoom}>Зал</div>
                  <div className={styles.colSlots}>Слоты</div>
                </div>

                <div className={styles.tableBody}>
                  {rooms.map((room) => (
                    <div key={room.id} className={styles.tableRow}>
                      <div className={styles.roomCell}>{room.name}</div>

                      <div className={styles.slotsCell}>
                        {templates.map((tpl) => {
                          const key = `${room.id}|${dateIso}|slot:${tpl.id}`;
                          const occ = occupiedMap.get(key);
                          const isOccupied = !!occ;

                          return (
                            <div
                              key={tpl.id}
                              className={styles.slotWrap}
                              onClick={(e) => handleSlotClick(e, room, dateIso, tpl)}
                            >
                              <button
                                type="button"
                                className={[
                                  styles.slotBtn,
                                  isOccupied ? styles.slotDisabled : "",
                                ].join(" ")}
                                // Убрали disabled - теперь все кнопки кликабельны
                              >
                                {tpl.label}
                              </button>
                            </div>
                          );
                        })}

                        {!loading && templates.length === 0 && (
                          <div className={styles.empty}>Нет активных шаблонов слотов.</div>
                        )}
                      </div>
                    </div>
                  ))}

                  {!loading && rooms.length === 0 && (
                    <div className={styles.empty}>Нет залов для отображения.</div>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {!loading && daysIso.length === 0 && (
          <div className={styles.empty}>В этой неделе нет выходных/праздников.</div>
        )}
      </div>

      <SlotTooltip 
        tooltip={tooltip} 
        onClose={closeTooltip}
        ref={tooltipRef}
      />
    </section>
  );
}